import styled from '@emotion/styled';
import { colors } from 'styles/theme/colors';

// The PDF canvas + floating pager primitives were promoted to the shared viewer —
// import them from `components/pdfViewer/pdfViewer.styles` (DL-61574).

const hideScrollbars = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
};

/**
 * Total height of file preview panels (e.g. {@link FileUploadSection}) so layout stays stable.
 */
export const DOCUMENT_PREVIEW_HEIGHT_PX = 440;

/**
 * Fills the host panel (e.g. {@link DocumentSheet}) so PDF/image preview can measure width/height and scale one page to fit.
 */
export const FilePreviewRoot = styled('div')({
  position: 'relative',
  flex: '1 1 0%',
  minWidth: 0,
  width: '100%',
  minHeight: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  alignSelf: 'stretch',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

/** Stacked PDF pages + visible scrollbar (certificate modal / scroll-sync pager). */
export const FilePreviewPdfScrollSyncArea = styled('div')({
  flex: 1,
  width: '100%',
  minHeight: 280,
  height: 'min(1200px, calc(100vh - 280px))',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  scrollbarWidth: 'thin',
  scrollbarColor: `${colors.grey[500]} ${colors.grey[100]}`,
  '&::-webkit-scrollbar': { width: 12 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: colors.grey[500],
    borderRadius: '8px',
    border: `3px solid ${colors.grey[100]}`,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: colors.grey[100],
    borderRadius: '8px',
  },
});

/** Used when {@link FilePreview} gets `pdfRemoteSrc` (http/https) — browser PDF viewer. */
export const PdfEmbeddedIframe = styled('iframe')({
  display: 'block',
  width: '100%',
  height: '100%',
  flex: '1 1 auto',
  minHeight: 0,
  border: 0,
  alignSelf: 'stretch',
  backgroundColor: colors.grey[50],
});

export const ImgPreviewClip = styled('div')({
  flex: '1 1 0%',
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  height: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  ...hideScrollbars,
});

export const PreviewImg = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
});

export const NonPdfFallback = styled('div')(p => ({
  flex: '1 1 0%',
  minHeight: 0,
  width: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // Spacing token s.sm (12px) read from the active theme when it carries the app
  // shape, with a static fallback under a bare MUI theme — `styles/theme/theme`
  // must not be imported here (it sits in an import cycle at module-eval time).
  padding: smToken(p.theme),
}));

type AppSmSpacingTheme = { s?: { sm?: string } };
const smToken = (theme: AppSmSpacingTheme) => theme.s?.sm ?? '12px';
